import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { BetsTable } from '../components/bets.tsx'
import { PnlChart } from '../components/charts.tsx'
import { Card, CardBody, CardHead, LoadError, PageHead, Seg, Skeleton, Stat } from '../components/ui.tsx'
import { usePageTitle } from '../layout/AppShell.tsx'
import { useApi } from '../lib/api.ts'
import { gbp, pnl } from '../lib/format.ts'
import type { PnlPayload, PnlRange, PnlShow } from '../../shared/types.ts'

const RANGES: { value: PnlRange; label: string }[] = [
  { value: '7d', label: '7D' },
  { value: '28d', label: '28D' },
  { value: '3m', label: '3M' },
  { value: '1y', label: '1Y' },
  { value: 'all', label: 'All time' },
]

const PAGE = 100

/** A figure in half a card: anything longer than "£999.99" steps down a size to fit. */
const fit = (text: string) => (text.length > 8 ? <span className="st-fit">{text}</span> : text)
const isRange = (v: string | null): v is PnlRange => RANGES.some((r) => r.value === v)
const isShow = (v: string | null): v is PnlShow => v === 'all' || v === 'open' || v === 'settled'

/**
 * P&L: every bet the account has, open and settled, with its result and profit, and the
 * running total over the chosen period. The period is on when a bet settled — that is
 * when its profit happened — so open bets are always listed whatever the period.
 *
 * This is where bets live, each marked with the bankroll round it counts against; a
 * round filter narrows to one round (Bankroll links here with it set). The list pages,
 * so a long round never has to arrive all at once.
 */
export function Pnl() {
  usePageTitle('P&L')
  const [params, setParams] = useSearchParams()
  const range: PnlRange = isRange(params.get('range')) ? (params.get('range') as PnlRange) : '28d'
  const show: PnlShow = isShow(params.get('show')) ? (params.get('show') as PnlShow) : 'all'
  const round = params.get('round')
  const [limit, setLimit] = useState(PAGE)
  // Changing a filter starts the list again from its first page.
  const set = (key: string, value: string | null) => {
    setLimit(PAGE)
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (value === null) next.delete(key)
        else next.set(key, value)
        return next
      },
      { replace: true },
    )
  }
  const q = new URLSearchParams({ range, show, limit: String(limit), ...(round ? { round } : {}) })
  const { data, error, loading, refreshing, reload } = useApi<PnlPayload>(`/pnl?${q}`, 60_000)

  const bets = data?.bets ?? []
  const t = data?.totals
  // While a new period loads, keep the old one on screen, dimmed, rather than flashing.
  const stale = loading || refreshing

  return (
    <main className="content">
      <PageHead
        title="P&L"
        sub="Every bet you have placed, what it made, and how the total has moved."
        actions={
          <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
            {data && data.rounds.length > 0 && (
              <select
                className="select"
                aria-label="Bankroll round"
                style={{ width: 'auto' }}
                value={round ?? ''}
                onChange={(e) => set('round', e.target.value || null)}
              >
                <option value="">All rounds</option>
                {data.rounds.map((r, i) => (
                  <option key={r.number} value={r.number}>
                    Round {r.number}
                    {i === 0 && !r.endedAt ? ' (current)' : ''} · {r.currency}
                  </option>
                ))}
              </select>
            )}
            <Seg label="Period" value={range} onChange={(v) => set('range', v)} options={RANGES} />
          </div>
        }
      />

      {error ? (
        <LoadError error={error} onRetry={reload} />
      ) : !data || !t ? (
        <Card>
          <CardBody>
            <Skeleton rows={8} />
          </CardBody>
        </Card>
      ) : (
        <div
          className="stack"
          style={{
            opacity: stale && (data.range !== range || String(data.round ?? '') !== (round ?? '')) ? 0.55 : 1,
            transition: 'opacity .15s',
          }}
        >
          <div className="grid cols-4">
            <Card pad>
              <Stat label="Profit" tone={t.profit > 0 ? 'pos' : t.profit < 0 ? 'neg' : null} value={pnl(t.profit)} />
            </Card>
            <Card pad className="split-stat">
              <Stat
                label="Return"
                tone={t.return > 0.00005 ? 'pos' : t.return < -0.00005 ? 'neg' : null}
                value={`${t.return >= 0 ? '+' : '−'}${Math.abs(t.return * 100).toFixed(1)}%`}
              />
              <Stat label="Turnover" value={fit(gbp(t.turnover))} />
            </Card>
            {/* Two equal halves, so won and lost read at a glance rather than as "1 · 0". */}
            <Card pad className="split-stat">
              <Stat label="Won" tone={t.won ? 'pos' : null} value={t.won} />
              <Stat
                label="Lost"
                tone={t.lost ? 'neg' : null}
                value={t.lost}
                sub={t.void ? `${t.void} void` : undefined}
              />
            </Card>
            <Card pad className="split-stat">
              <Stat label="Open bets" value={t.open} />
              <Stat label="Exposure" value={fit(gbp(t.exposure))} />
            </Card>
          </div>

          <Card>
            <CardHead title="Profit and loss" sub="Running total of settled bets" />
            <CardBody>
              {t.won + t.lost + t.void === 0 ? (
                <div className="u-faint" style={{ padding: '24px 0' }}>
                  Nothing settled in this period yet. Bets settle within about ten minutes of their game ending.
                </div>
              ) : (
                <PnlChart data={data.series} />
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHead
              title="Bets"
              sub={data.round !== null ? `Round ${data.round}` : 'All rounds'}
              right={
                <Seg
                  label="Show"
                  value={show}
                  onChange={(v) => set('show', v)}
                  options={[
                    { value: 'all', label: `All ${data.counts.all}` },
                    { value: 'open', label: `Open ${data.counts.open}` },
                    { value: 'settled', label: `Settled ${data.counts.settled}` },
                  ]}
                />
              }
            />
            <CardBody flush>
              {bets.length === 0 ? (
                <div style={{ padding: 24 }} className="u-faint">
                  {show === 'open' ? 'No open bets.' : 'No bets in this period.'}
                </div>
              ) : (
                <>
                  <BetsTable bets={bets} />
                  {data.more && (
                    <div style={{ padding: 14, textAlign: 'center' }}>
                      <button className="btn outline" disabled={loading} onClick={() => setLimit((n) => n + PAGE)}>
                        {loading ? 'Loading…' : `Show ${PAGE} more`}
                      </button>
                    </div>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </div>
      )}
    </main>
  )
}
